import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { installDiagnostics } from "./lib/diagnostics";
import { startVersionCheck } from "./lib/versionCheck";

// Install diagnostics FIRST so we capture every nav + error from the start.
installDiagnostics();

// Poll for new deployed builds every 2 minutes and auto-reload.
startVersionCheck();

/**
 * Hard kill-switch for any legacy Service Worker / Cache Storage.
 *
 * The app is intentionally NOT a PWA. Older builds shipped vite-plugin-pwa
 * which installed a SW on users' browsers. That SW kept serving stale assets
 * even after new deploys, causing apparent "rollbacks" + React error #301.
 *
 * This block runs on every page load to:
 *   1. Unregister every active Service Worker registration
 *   2. Wipe every Cache Storage bucket
 *   3. If we just removed a controller that was hijacking requests,
 *      reload ONCE so the next load goes straight to the network.
 */
async function purgeLegacyPwa() {
  let hadRegistrations = false;
  let hadCaches = false;

  if ("serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      if (regs.length > 0) {
        hadRegistrations = true;
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
    } catch { /* noop */ }
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      if (keys.length > 0) hadCaches = true;
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    } catch { /* noop */ }
  }

  // If we just removed a SW or any cached entries, force ONE clean reload
  // so the browser re-requests index.html + latest hashed assets from origin.
  if ((hadRegistrations || hadCaches) && !sessionStorage.getItem("pwa:purged")) {
    sessionStorage.setItem("pwa:purged", "1");
    const url = new URL(window.location.href);
    url.searchParams.set("_r", Date.now().toString());
    window.location.replace(url.toString());
  }
}

void purgeLegacyPwa();

createRoot(document.getElementById("root")!).render(<App />);
